<template>
  <form>
    <div class="row mb-2">
      <div class="col-sm-12">

        <div class="row mt-2">
          <label for="name" class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.name") }}</label>
          <div class="col-sm-8">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors(`name`)">
              <input :class="parentProps.class" v-model="form.name" type="text" class="form-control " id="name">
            </FormInputWrapper>
          </div>
        </div>

        <div class="row mt-2">
          <label for="email" class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.email") }}</label>
          <div class="col-sm-8">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors(`email`)">
              <input v-model="form.email" :class="parentProps.class" :readonly="!!form.id" type="text" class="form-control " id="email">
            </FormInputWrapper>
          </div>
        </div>

        <div class="row mt-2">
          <label for="is_blocked" class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.is_blocked") }}</label>
          <div class="col-sm-8">
            <div class="form-check form-switch d-flex align-items-center">
              <input  v-model="form.isBlocked" class="form-check-input" type="checkbox" id="is_blocked">
            </div>
          </div>
        </div>

        <div class="row mt-2" v-if="!form.id">
          <label for="createInvite" class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.create_invite") }}</label>
          <div class="col-sm-8">
            <div class="form-check form-switch d-flex align-items-center">
              <input  v-model="form.createInvite" class="form-check-input" type="checkbox" id="createInvite">
            </div>
          </div>
        </div>

        <div class="row mt-2" v-if="(form.id || form.createInvite === false) && (isTrader || store.getters.hasPermission(UserPermission.RESET_TRADER_PASSWORD))">
          <label for="password" class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.password") }}</label>
          <div class="col-sm-8">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors(`password`)" >
              <div class="input-group" :class="parentProps.class">
                <input v-model="form.password" :class="parentProps.class" type="text" class="form-control " id="password">
                <button @click.prevent="form.password = helper.generatePassword()" type="button" class="btn btn-outline-secondary"><i class="fa fa-rotate"></i></button>
              </div>
            </FormInputWrapper>
          </div>
        </div>

        <div v-if="isAdmin" class="row mt-2">
          <label for="traderId" class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.trader") }}</label>
          <div class="col-sm-8">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors(`traderId`)">
              <Multiselect id="traderId"
                           class=""
                           :class="parentProps.class"
                           v-model="form.trader"
                           track-by="id"
                           :disabled="!!form.id"
                           :options="traders"
                           :custom-label="(opt) => `${opt.name} (${opt.email})`"
                           :custom-value="(opt) => opt.id"
                           select-label="Select"
                           placeholder=""
                           open-direction="bottom"
                           :multiple="false"
                           :searchable="true"
                           :internal-search="true"
                           :clear-on-select="false"
                           :close-on-select="true"
                           :options-limit="100"
                           :limit="1000"
                           :show-no-results="false"
                           :hide-selected="false"
                           @search-change="asyncFindTraders"
                           :loading="isTradersSelectLoading"
              >
              </Multiselect>
            </FormInputWrapper>
          </div>
        </div>
        <div class="row mt-2" v-if="form.id">
          <label for="2fa" class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.2fa") }}</label>
          <div class="col-sm-8 align-items-center d-flex">
            <span v-if="form?.is2faEnabled" class="text-success">{{ helper.trans('labels.enabled')}}</span>
            <span v-if="!form?.is2faEnabled" class="text-warning"><i class="fa fa-warning"></i> {{ helper.trans('labels.disabled')}}</span>
            <button v-if="form?.is2faEnabled" type="button" class="btn btn-danger ms-2 btn-sm text-capitalize-first" @click.prevent="disable2fa(form.id)">{{ helper.trans("labels.disable")}}</button>
          </div>
        </div>
      </div>
    </div>
    <ConfirmModal ref="twoFaConfirmModal"></ConfirmModal>

  </form>
</template>

<script lang="ts">
import {defineComponent, toRefs, reactive, computed, PropType, Prop, onMounted, ref} from "vue";
import {useStore} from "vuex";
import {helper} from "@/service/CommonHelper";
import {numberFormatter} from "@/service/NumberFormatter";
import Multiselect from "vue-multiselect";
import ValidationErrorDTO from "@/models/ValidationErrorDTO";
import APIClient from "@/service/APIClient";
import {useNotification} from "@kyvg/vue3-notification";
import FormInputWrapper from "@/components/FormInputWrapper.vue";
import PaginationDTO from "@/models/PaginationDTO";
import TraderOperatorFormDTO from "@/models/form/TraderOperatorFormDTO";
import TraderDTO from "@/models/TraderDTO";
import ConfirmModal, {ConfirmModalProps} from "@/components/ConfirmModal.vue";
import {UserPermission} from "@/models/enum/UserPermission";

const components = {
  Multiselect,
  FormInputWrapper,
  ConfirmModal
}

const api = new APIClient();
const notification = useNotification()

export default defineComponent({
  components,
  setup() {
    const twoFaConfirmModal = ref<InstanceType<typeof ConfirmModal> | null>(null)
    const store = useStore();
    const state = reactive({
      traders: [] as TraderDTO[],
      isTradersSelectLoading: false,
      errors: {} as ValidationErrorDTO[]|any,
      form: {} as TraderOperatorFormDTO,
    });

    const setErrors = (data: ValidationErrorDTO[]) => {
      state.errors = {};
      for (const error of data) {
        if (!state.errors.hasOwnProperty(error.field)) {
          state.errors[error.field] = [];
        }
        state.errors[error.field].push(error)
      }
    }

    const save = (): Promise<any> => {
      setErrors([]);
      const form = getForm();

      let promise;
      if (form.id) {
        const passwordPromise = form.password
          ? api.call.traderOperators.changePassword(form.id, form.password)
          : Promise.resolve();

        if (store.getters.isOperator) {
          promise = passwordPromise;
        } else {
          const data = {
            ...form,
            password: null,
          }

          promise = api.call.traderOperators.update(form.id, data).then(() => passwordPromise);
        }

        promise = promise.then((resp) => {
          notification.notify({title: helper.trans('users.notice.updated')});

          return resp;
        })
      } else {
        const data = {
          ...form,
          password: form.createInvite ? null : (form.password ? form.password : null),
          createInvite: form.createInvite,
          traderId: form.trader?.id
        }

        promise = api.call.traderOperators.create(data)
          .then((resp) => {
            notification.notify({title: helper.trans('users.notice.created')});

            return resp;
          })
      }

      promise
        .catch((e: any) => {
          if (e.response && e.request && e.request.status === 400 && e.response.data) {
            setErrors(e.response.data)
          } else {
            console.log(e);
          }

        })

      return promise
    }

    const getErrors = (path: string) => {
      if (!state.errors.hasOwnProperty(path)) {
        return [];
      }

      return state.errors[path]
    }

    const hasErrors = (path: string) => {
      return state.errors[path]?.length > 0
    }

    const getForm = (): TraderOperatorFormDTO => {
      return state.form
    }

    const setForm = (form: TraderOperatorFormDTO) => {
      asyncFindTraders('')
      state.errors = {}
      state.form = {
        ...form,
      }
    }

    const asyncFindTraders = async (query: string) => {
      state.isTradersSelectLoading = true;
      state.traders = (await api.traders({limit: 100, offset: 0} as PaginationDTO, {query: query})).items;
      state.isTradersSelectLoading = false
    }

    onMounted(() => {
    })

    const disable2fa = async (id: number) => {
      twoFaConfirmModal.value?.show({
        title: helper.trans('labels.2fa_disable_confirmation'),
        okTitle: null,
        cancelTitle: null,
        onOkClick: async () => {
          try {
            await api.user2faDisable(id);
            notification.notify({title: helper.trans('labels.2fa_disabled')});
            state.form.is2faEnabled = false
          } finally {
          }
        }
      } as ConfirmModalProps);
    }

    return {
      ...toRefs(state),
      getForm,
      setForm,
      store,
      helper,
      numberFormatter,
      setErrors,
      getErrors,
      hasErrors,
      save,
      asyncFindTraders,
      isAdmin: computed(() => store.getters.isAdmin),
      isTrader: computed(() => store.getters.isTrader),
      disable2fa,
      twoFaConfirmModal,
      UserPermission
    }
  }
})
</script>
